import React, {useEffect, useRef, useState} from "react";
import {withAuth} from "../../hoc/Auth";
import {Parser} from "json2csv";
import * as XLSX from "xlsx";
import * as Sentry from "@sentry/browser";
import Api from "../../Api";
import AntiFraudFilters from "../../Components/AntiFraudFilters";
import AntiFraudTable from "../../Components/AntiFraudTable";
import Pagination from "react-js-pagination";
import classes from "./antifraudLog.module.css";

var ACCESS_ANTIFRAUD_LOG = 262144;

function checkAccess(accountAccessSettings, globalPrivileges, bitMask) {
    if (globalPrivileges !== null && globalPrivileges !== undefined) {
        return (globalPrivileges & bitMask) > 0;
    }
    if (Array.isArray(accountAccessSettings)) {
        return accountAccessSettings.some(function (el) {
            return (el.privileges & bitMask) > 0;
        });
    }
    return false;
}

var AntiFraudLog = withAuth(function (props) {
    var token = props.token;
    var accountAccessSettings = props.accountAccessSettings;
    var globalPrivileges = props.globalPrivileges;

    var mountedRef = useRef(true);
    var [data, setData] = useState([]);
    var [count, setCount] = useState(0);
    var [currentPage, setCurrentPage] = useState(1);
    var [itemsPerPage] = useState(20);
    var [filters, setFilters] = useState({});
    var [isLoading, setIsLoading] = useState(false);
    var [isExporting, setIsExporting] = useState(false);

    useEffect(function () {
        return function () {
            mountedRef.current = false;
        };
    }, []);

    useEffect(function () {
        loadData(1, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function loadData(page, filtersOverride) {
        setIsLoading(false);
        var payload = Object.assign({}, filtersOverride || filters, {
            page: page || currentPage,
            itemsPerPage: itemsPerPage
        });

        new Api({token}).getAntiFraudMerchantLogs(payload).then(function (r) {
            if (!mountedRef.current) return;
            setData(r && r.data ? r.data : []);
            setCount(r && r.count ? Number(r.count) : 0);
            setIsLoading(true);
        }).catch(function (err) {
            if (!mountedRef.current) return;
            Sentry.captureException(err);
            setData([]);
            setCount(0);
            setIsLoading(true);
        });
    }

    function handleApplyFilters(newFilters) {
        setFilters(newFilters);
        setCurrentPage(1);
        loadData(1, newFilters);
    }

    function handlePageChange(page) {
        setCurrentPage(page);
        loadData(page, filters);
    }

    function handleExportCsv() {
        if (isExporting) return;
        setIsExporting(true);

        var payload = Object.assign({}, filters);

        new Api({token}).exportAntiFraudLogs(payload).then(function (r) {
            if (!mountedRef.current) return;
            try {
                var universalBOM = "\uFEFF";
                var rows = r.data.map(function (row) {
                    var obj = {};
                    r.fields.forEach(function (field, i) { obj[field] = row[i]; });
                    return obj;
                });
                var json2csv = new Parser({delimiter: ";", fields: r.fields});
                var csv = json2csv.parse(rows);

                var a = document.createElement("a");
                var date = new Date().toISOString().slice(0, 10);
                a.setAttribute("href", "data:text/csv; charset=utf-8," + encodeURIComponent(universalBOM + csv));
                a.setAttribute("download", "antifraud_log_" + date + ".csv");
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } catch (e) {
                Sentry.captureException(e);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
        }).finally(function () {
            setIsExporting(false);
        });
    }

    function handleExportExcel() {
        if (isExporting) return;
        setIsExporting(true);

        var payload = Object.assign({}, filters);

        new Api({token}).exportAntiFraudLogs(payload).then(function (r) {
            if (!mountedRef.current) return;
            try {
                var sheetData = [r.fields].concat(r.data);
                var worksheet = XLSX.utils.aoa_to_sheet(sheetData);
                var workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Antifraud Log");

                var wbout = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
                var blob = new Blob([wbout], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                });

                var a = document.createElement("a");
                var date = new Date().toISOString().slice(0, 10);
                a.href = URL.createObjectURL(blob);
                a.download = "antifraud_log_" + date + ".xlsx";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(a.href);
            } catch (e) {
                Sentry.captureException(e);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
        }).finally(function () {
            setIsExporting(false);
        });
    }

    if (!checkAccess(accountAccessSettings, globalPrivileges, ACCESS_ANTIFRAUD_LOG)) {
        return (
            <div className={`${classes["container-fluid"]} d-flex flex-column min-vh-100`}>
                <div className="row flex-grow-1">
                    <div className="col-12 pt-2">
                        <div className="d-flex justify-content-center align-items-center" style={{minHeight: "60vh"}}>
                            <div className="text-center">
                                <h4>Нет доступа</h4>
                                <p className="text-muted">У вас нет прав для просмотра этого раздела</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${classes["container-fluid"]} d-flex flex-column min-vh-100`}>
            <div className="row flex-grow-1">

                <div className="col-12 pt-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>Лог антифрод-проверок</h4>
                    </div>

                    <AntiFraudFilters
                        onApplyFilters={handleApplyFilters}
                        accountAccessSettings={accountAccessSettings}
                        isLoading={!isLoading}
                        token={token}
                    />

                    <div className={classes.ExportBar}>
                        <button
                            className={classes.ExportBtn}
                            onClick={handleExportCsv}
                            disabled={isExporting || count === 0}
                        >
                            Экспорт CSV
                        </button>
                        <button
                            className={classes.ExportBtn}
                            onClick={handleExportExcel}
                            disabled={isExporting || count === 0}
                        >
                            Экспорт Excel
                        </button>
                    </div>

                    <AntiFraudTable
                        data={data}
                        isLoading={isLoading}
                    />

                    <div className={classes.PaginationWrap}>
                        <Pagination
                            firstPageText={"⇤"}
                            prevPageText={"⇠"}
                            lastPageText={"⇥"}
                            nextPageText={"⇢"}
                            activeLinkClass={classes.ActiveLink}
                            activePage={currentPage}
                            itemsCountPerPage={itemsPerPage}
                            totalItemsCount={count}
                            pageRangeDisplayed={10}
                            onChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AntiFraudLog;



// WEBPACK FOOTER //
// ./src/PagesOfUser/AntiFraudLog/index.js